import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { SidebarTab } from '../types/cockpit';
import LexAIService from '../services/api';
import { useAuthToken } from '../hooks/useAuthToken';
import { useSidebarData } from '../hooks/useSidebarData';
import CockpitHeader from './CockpitHeader';
import CockpitMainContent from './CockpitMainContent';
import { SearchResult } from './SearchResults';
import { useOpenInCanvas } from '../contexts/GlobalCanvasContext';

/**
 * Props for the CockpitLayout component
 * Currently empty as this component manages its own state internally
 * @interface CockpitLayoutProps
 */
interface CockpitLayoutProps {}

/**
 * Main layout component for the LexAI cockpit/dashboard
 * Orchestrates sidebar navigation, content display, and real-time data updates
 * Manages connection status, task/email counts, and sidebar collapse states
 *
 * @component
 * @param {CockpitLayoutProps} props - Component props (currently empty)
 * @returns {JSX.Element} Rendered cockpit layout
 *
 * @example
 * ```tsx
 * <CockpitLayout />
 * ```
 *
 * Features:
 * - Real-time task and email count updates (every 60 seconds)
 * - Connection status monitoring (every 30 seconds)
 * - Responsive sidebar management
 * - Integrated header and main content areas
 */
const CockpitLayout: React.FC<CockpitLayoutProps> = () => {
  const { accessToken, isLoading: tokenLoading } = useAuthToken();
  const [activeTab, setActiveTab] = useState<SidebarTab>('tasks');
  const [leftSidebarCollapsed, setLeftSidebarCollapsed] = useState(false);
  const [rightSidebarCollapsed, setRightSidebarCollapsed] = useState(true); // Start collapsed
  const [canvasContent, setCanvasContent] = useState<any>(null);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'checking'>('checking');
  const [activeTaskCount, setActiveTaskCount] = useState(0);
  const [activeEmailCount, setActiveEmailCount] = useState(0);
  const [searchExpanded, setSearchExpanded] = useState(false);

  // Get sidebar data for search functionality
  const { sidebarData, fullTasksData, fullEmailsData, loadSidebarData } = useSidebarData();

  // Get canvas functions for opening search results
  const { openTask, openEmail, openOpportunity, openPerson } = useOpenInCanvas();
  

  /**
   * Refreshes active task and email counts from the API
   * Updates sidebar badges and maintains real-time data accuracy
   * Filters tasks to show only incomplete ones and emails to show only unread ones
   *
   * @returns {Promise<void>}
   *
   * @example
   * ```typescript
   * await refreshCounts(); // Updates taskCount and emailCount state
   * ```
   */
  const refreshCounts = useCallback(async () => {
    if (!tokenLoading && accessToken) {
      try {
        const [tasks, emails] = await Promise.all([
          LexAIService.getDashboardTasks(),
          LexAIService.getDashboardEmails()
        ]);

        const activeTasks = tasks?.filter(task => task && !task.isCompleted) || [];
        const unreadEmails = emails?.filter(email => email && !email.isRead) || [];

        setActiveTaskCount(activeTasks.length);
        setActiveEmailCount(unreadEmails.length);
      } catch (error) {
        console.error('Failed to refresh counts:', error);
        setActiveTaskCount(0);
        setActiveEmailCount(0);
      }
    }
  }, [accessToken, tokenLoading]);

  /**
   * Checks the connection status to the LexAI backend services
   * Updates connection status state for UI feedback (connected/disconnected/checking)
   * Used for periodic health monitoring of the API endpoints
   *
   * @returns {Promise<void>}
   *
   * @example
   * ```typescript
   * await checkConnection(); // Updates connectionStatus state
   * ```
   */
  const checkConnection = useCallback(async () => {
    try {
      await LexAIService.getDashboardSummary();
      setConnectionStatus('connected');
    } catch (error) {
      setConnectionStatus('disconnected');
    }
  }, []);

  // Load initial data and set up periodic updates
  useEffect(() => {
    if (!tokenLoading && accessToken) {
      // Load initial counts
      refreshCounts();

      // Set up periodic count updates (every 60 seconds)
      const countInterval = setInterval(refreshCounts, 60000);

      // Set up connection status checks (every 30 seconds)
      checkConnection();
      const connectionInterval = setInterval(checkConnection, 30000);

      return () => {
        clearInterval(countInterval);
        clearInterval(connectionInterval);
      };
    }
  }, [accessToken, tokenLoading, refreshCounts, checkConnection]);

  // Load sidebar data for search when search is expanded
  useEffect(() => {
    if (searchExpanded && !tokenLoading && accessToken) {
      // Load all tabs for search functionality
      const loadAllData = async () => {
        await Promise.all([
          loadSidebarData('clients'),
          loadSidebarData('deals'),
          loadSidebarData('tasks'),
          loadSidebarData('emails')
        ]);
      };
      loadAllData();
    }
  }, [searchExpanded, accessToken, tokenLoading, loadSidebarData]);

  /**
   * Handles content selection from the sidebar
   * Sets canvas content and automatically expands right sidebar to show selected content
   * Acts as the main bridge between sidebar navigation and canvas display
   *
   * @param {any} content - The content selected from sidebar (task, email, file, etc.)
   *
   * @example
   * ```typescript
   * const taskContent = { type: 'task', id: 'task-123', title: 'Review proposal' };
   * handleContentSelect(taskContent); // Opens task in canvas and shows right sidebar
   * ```
   */
  const handleContentSelect = useCallback((content: any) => {
    setCanvasContent(content);
    if (content) {
      setRightSidebarCollapsed(false); // Show right sidebar when content is selected
    }
  }, []);

  const handleRightSidebarToggle = useCallback(() => {
    setRightSidebarCollapsed(!rightSidebarCollapsed);
  }, [rightSidebarCollapsed]);

  const handleSearchToggle = useCallback(() => {
    setSearchExpanded(!searchExpanded);
  }, [searchExpanded]);

  const handleSearchClose = useCallback(() => {
    setSearchExpanded(false);
  }, []);

  /**
   * Handles selection of a search result
   * Opens the selected item in the appropriate canvas view
   */
  const handleSearchResultSelect = useCallback((result: SearchResult) => {
    console.log('Search result selected:', result);

    switch (result.type) {
      case 'task':
        if (result.data) {
          openTask(result.data);
        }
        break;
      case 'email':
        if (result.data) {
          openEmail(result.data);
        }
        break;
      case 'deal':
        if (result.data?.dealData) {
          openOpportunity(result.data.dealData);
        } else if (result.data) {
          openOpportunity(result.data);
        }
        break;
      case 'client':
        if (result.data) {
          const clientData = {
            ...result.data,
            name: result.data.name || result.data.displayName || result.title,
            displayName: result.data.displayName || result.data.name || result.title,
            status: result.data.status || result.subtitle || 'Active',
            id: result.data.id || result.id
          };
          openPerson(clientData);
        }
        break;
      default:
        console.log('Unknown result type:', result.type);
    }

    // Close search after selection
    setSearchExpanded(false);
  }, [openTask, openEmail, openOpportunity, openPerson]);

  // Remove the integrated tab component - no longer needed

  console.log('🏢 CockpitLayout rendering');

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        backgroundColor: 'var(--obsidian-bg)',
        color: 'var(--obsidian-text-primary)'
      }}
      role="application"
      aria-label="LexAI Dashboard"
    >
      <CockpitHeader
        leftSidebarCollapsed={leftSidebarCollapsed}
        rightSidebarCollapsed={rightSidebarCollapsed}
        searchExpanded={searchExpanded}
        connectionStatus={connectionStatus}
        onLeftSidebarToggle={() => setLeftSidebarCollapsed(!leftSidebarCollapsed)}
        onRightSidebarToggle={handleRightSidebarToggle}
        onSearchToggle={handleSearchToggle}
        onSearchClose={handleSearchClose}
        canvasContent={canvasContent}
        sidebarData={sidebarData}
        fullTasksData={fullTasksData}
        fullEmailsData={fullEmailsData}
        onSearchResultSelect={handleSearchResultSelect}
      />

      <CockpitMainContent
        leftSidebarCollapsed={leftSidebarCollapsed}
        rightSidebarCollapsed={rightSidebarCollapsed}
        activeTab={activeTab}
        canvasContent={canvasContent}
        onContentSelect={handleContentSelect}
        onCountChange={refreshCounts}
        taskCount={activeTaskCount}
        emailCount={activeEmailCount}
        onUpdateTask={async () => {
          setActiveTab(activeTab); // Trigger a refresh
          await refreshCounts();
        }}
        onUpdateEmail={async () => {
          setActiveTab(activeTab); // Trigger a refresh
        }}
        onTabChange={setActiveTab}
      />
    </div>
  );
};

export default CockpitLayout;
